import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get } from '@app/api/typed';
import type { Decision, DecisionPage } from '@app/main/Decisions';
import { conversationRange, type ConversationWindow } from './ChatEntryModel';

/**
 * The PDP decisions of a Chat question (aim-6ph.9, and the id of the question since aim-6ph.10).
 *
 * The trail is a reading, and it reads the decision history in the Postgres (rule 11 of the CLAUDE.md). Before the
 * first ingestion of a tenant the backend serves the list from the executor, as in Decisions (T16). The question
 * itself stays an action, and it still goes to the executor.
 *
 * # BY THE ID OF THE QUESTION, AND BY THE PERSON (aim-6ph.10, decisions 6 and 9 of 11/10/2026)
 *
 * The console creates an id for each question, and each decision of the question comes back with it. The trail asks
 * for the decisions of the id AND the person: an id forged by another person stays out. In a separate line, it shows
 * the decisions of the same person, in the window of the question, that came without the id: an agent that does not
 * forward the header, an older executor, a gateway without the change. They can be of another question, and the line
 * says so. Without the id (an answer of an executor before it), the trail reads by the person and the window, as
 * before.
 *
 * # THE SEARCH LASTS 2 MINUTES AFTER THE END OF THE ANSWER (decision 8 of the mrolim, 11/10/2026)
 *
 * With the id, a late decision does not mix with another question, so the search can wait longer than the 15 s of
 * aim-6ph.9. It reads every 5 s, the same pace of the history, and the list grows while it lasts. At the deadline one
 * last read runs, and after it the trail offers «Atualizar», which reads once more.
 */

export const CONVERSATION_SEARCH_MS = 120_000;
export const CONVERSATION_POLL_MS = 5_000;
/** The page of the search: the `limit` default of the contract. More than this in a conversation is in Decisions. */
export const CONVERSATION_LIMIT = 50;

/**
 * - `searching`: the search runs, and no decision of the question came yet;
 * - `found`: at least one decision of the question came (the list can still grow while the search runs);
 * - `none`: the search ended without a decision of the question;
 * - `error`: the read of the history failed, and no decision came.
 */
export type ConversationState = 'searching' | 'found' | 'none' | 'error';

export interface ConversationDecisions {
  state: ConversationState;
  /** The decisions of the question, in the order of the conversation: the oldest first. */
  items: Decision[];
  /**
   * The decisions of the same person in the window that came without the id, in the same order. Always empty when the
   * question has no id: then `items` is already the read by the person and the window.
   */
  withoutId: Decision[];
  error: ApiError | null;
  /** `true` after the deadline: the trail offers «Atualizar». */
  ended: boolean;
  /** «Atualizar»: one more read of the two lists. */
  refresh: () => void;
  /** `true` while a read runs. */
  reading: boolean;
}

function inOrder(page: DecisionPage | undefined): Decision[] {
  // `Cursor.items` is `unknown[]` in the generated contract; the decisions page specializes it here.
  return ((page?.items ?? []) as Decision[])
    .slice()
    .sort((a, b) => a.ts.localeCompare(b.ts) || a.request_id.localeCompare(b.request_id));
}

export function useConversationDecisions(window: ConversationWindow | null): ConversationDecisions {
  const sub = window?.sub ?? '';
  const conversationId = window?.conversationId ?? '';
  const range = window ? conversationRange(window) : null;
  const deadline = window ? window.finishedAt.getTime() + CONVERSATION_SEARCH_MS : 0;
  const [phase, setPhase] = useState<'searching' | 'final'>(() =>
    Date.now() < deadline ? 'searching' : 'final',
  );
  useEffect(() => {
    if (!deadline) return undefined;
    const left = deadline - Date.now();
    if (left <= 0) {
      setPhase('final');
      return undefined;
    }
    setPhase('searching');
    const timer = setTimeout(() => setPhase('final'), left);
    return () => clearTimeout(timer);
  }, [deadline]);

  // The phase is in the key: the deadline is a new read, and `keepPreviousData` keeps the list on the screen while it
  // runs, instead of emptying it.
  const polling = phase === 'searching' ? CONVERSATION_POLL_MS : false;
  const ofQuestion = useQuery<DecisionPage, ApiError>({
    queryKey: ['decisions', 'conversation', conversationId, sub, range?.since ?? '', range?.until ?? '', phase],
    queryFn: () =>
      get('/api/v1/decisions', {
        params: conversationId
          ? // The id and the person; the window starts at the question and has no end, because the id already
            // separates this question from the next one.
            { conversation_id: conversationId, sub, since: range!.since, limit: CONVERSATION_LIMIT }
          : { sub, since: range!.since, until: range!.until, limit: CONVERSATION_LIMIT },
      }),
    enabled: Boolean(window),
    refetchInterval: polling,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
  const withoutIdQuery = useQuery<DecisionPage, ApiError>({
    queryKey: ['decisions', 'conversation-without-id', sub, range?.since ?? '', range?.until ?? '', phase],
    queryFn: () =>
      get('/api/v1/decisions', {
        params: {
          sub,
          since: range!.since,
          until: range!.until,
          without_conversation_id: true,
          limit: CONVERSATION_LIMIT,
        },
      }),
    enabled: Boolean(window) && Boolean(conversationId),
    refetchInterval: polling,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => inOrder(ofQuestion.data), [ofQuestion.data]);
  const withoutId = useMemo(
    () => (conversationId ? inOrder(withoutIdQuery.data) : []),
    [conversationId, withoutIdQuery.data],
  );

  const { refetch: refetchQuestion } = ofQuestion;
  const { refetch: refetchWithoutId } = withoutIdQuery;
  const refresh = useCallback(() => {
    void refetchQuestion();
    if (conversationId) void refetchWithoutId();
  }, [conversationId, refetchQuestion, refetchWithoutId]);

  let state: ConversationState;
  if (items.length > 0) state = 'found';
  else if (ofQuestion.isError) state = 'error';
  else if (phase === 'searching' || ofQuestion.isFetching || ofQuestion.isPending) state = 'searching';
  else state = 'none';
  return {
    state,
    items,
    withoutId,
    error: ofQuestion.error ?? null,
    ended: phase === 'final',
    refresh,
    reading: ofQuestion.isFetching || withoutIdQuery.isFetching,
  };
}
